import { z } from "zod";

export interface SiteAddress {
  line1: string;
  line2: string;
  state: string;
  pincode: string;
  full: string;
}

export interface SiteContact {
  phone: string;
  phoneHref: string;
  email: string;
  csrEmail: string;
  hours: string;
}

export interface SiteConfig {
  name: string;
  shortName: string;
  tagline: string;
  legalStatus: string;
  description: string;
  url: string;
  displayHost: string;
  address: SiteAddress;
  contact: SiteContact;
  mapsUrl: string;
  mapEmbedUrl: string;
  donate: {
    taxBenefitNote: string;
  };
}

export const siteDefaults: SiteConfig = {
  name: "Aja Abhaya Hastham Foundation",
  shortName: "AJA Abhaya Hastham",
  tagline: "Making a difference, one life at a time.",
  legalStatus: "A Section 8 Company",
  description:
    "AJA Abhaya Hastham is dedicated to creating positive change through comprehensive community development programs.",
  url: "https://www.ajaah.org",
  displayHost: "www.ajaah.org",
  address: {
    line1: "A Block, 4th Floor, Q City",
    line2: "Gachibowli, Hyderabad",
    state: "Telangana",
    pincode: "500032",
    full: "Aja Abhaya Hastham Foundation, A Block, 4th Floor, Q City, Gachibowli, Hyderabad, Telangana 500032",
  },
  contact: {
    phone: "+91 9966060369",
    phoneHref: "tel:+919966060369",
    email: "csr@ajaah.org",
    csrEmail: "csr@ajaah.org",
    hours: "Mon - Fri: 9:00 AM - 6:00 PM",
  },
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=Q+City+Gachibowli+Hyderabad+Telangana+500032",
  mapEmbedUrl:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.454986933009!2d78.36131807501205!3d17.44087898350063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcbf194338ebf23%3A0x67fecc000a08889d!2sQ%20City%2C%20Gachibowli%2C%20Hyderabad%2C%20Telangana%20500032!5e0!3m2!1sen!2sin!4v1710000000000!5m2!1sen!2sin",
  donate: {
    taxBenefitNote:
      "Eligible for tax benefits under Section 80G. A receipt will be emailed after successful payment.",
  },
};

const emailSchema = z.string().email();

function readEnv(key: string, fallback: string): string {
  const raw = process.env[key]?.trim() ?? "";
  return raw.length > 0 ? raw : fallback;
}

function readHttpsUrl(key: string, fallback: string): string {
  const raw = process.env[key]?.trim() ?? "";
  if (!raw) return fallback;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? raw : fallback;
  } catch {
    return fallback;
  }
}

function readEmail(key: string, fallback: string): string {
  const raw = process.env[key]?.trim() ?? "";
  if (!raw) return fallback;
  return emailSchema.safeParse(raw).success ? raw : fallback;
}

function phoneToHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, "");
  return `tel:${digits}`;
}

function composeAddressFull(
  name: string,
  line1: string,
  line2: string,
  state: string,
  pincode: string
): string {
  return `${name}, ${line1}, ${line2}, ${state} ${pincode}`;
}

function hostFromUrl(url: string, fallback: string): string {
  try {
    return new URL(url).host || fallback;
  } catch {
    return fallback;
  }
}

/** Site identity and contact info from NEXT_PUBLIC_SITE_* env vars (with code defaults). */
export function getSiteConfig(): SiteConfig {
  const name = readEnv("NEXT_PUBLIC_SITE_NAME", siteDefaults.name);
  const shortName = readEnv("NEXT_PUBLIC_SITE_SHORT_NAME", siteDefaults.shortName);
  const url = readHttpsUrl("NEXT_PUBLIC_SITE_URL", siteDefaults.url);

  const line1 = readEnv(
    "NEXT_PUBLIC_SITE_ADDRESS_LINE1",
    siteDefaults.address.line1
  );
  const line2 = readEnv(
    "NEXT_PUBLIC_SITE_ADDRESS_LINE2",
    siteDefaults.address.line2
  );
  const state = readEnv(
    "NEXT_PUBLIC_SITE_ADDRESS_STATE",
    siteDefaults.address.state
  );
  const pincode = readEnv(
    "NEXT_PUBLIC_SITE_ADDRESS_PINCODE",
    siteDefaults.address.pincode
  );

  const addressFullEnv = process.env.NEXT_PUBLIC_SITE_ADDRESS_FULL?.trim() ?? "";
  const addressFull =
    addressFullEnv.length > 0
      ? addressFullEnv
      : composeAddressFull(name, line1, line2, state, pincode);

  const phone = readEnv("NEXT_PUBLIC_SITE_PHONE", siteDefaults.contact.phone);
  const phoneHrefEnv = process.env.NEXT_PUBLIC_SITE_PHONE_HREF?.trim() ?? "";
  const phoneHref =
    phoneHrefEnv.length > 0 ? phoneHrefEnv : phoneToHref(phone);

  return {
    name,
    shortName,
    tagline: siteDefaults.tagline,
    legalStatus: siteDefaults.legalStatus,
    description: siteDefaults.description,
    url,
    displayHost: hostFromUrl(url, siteDefaults.displayHost),
    address: {
      line1,
      line2,
      state,
      pincode,
      full: addressFull,
    },
    contact: {
      phone,
      phoneHref,
      email: readEmail("NEXT_PUBLIC_SITE_EMAIL", siteDefaults.contact.email),
      csrEmail: readEmail(
        "NEXT_PUBLIC_SITE_CSR_EMAIL",
        siteDefaults.contact.csrEmail
      ),
      hours: readEnv("NEXT_PUBLIC_SITE_HOURS", siteDefaults.contact.hours),
    },
    mapsUrl: readHttpsUrl("NEXT_PUBLIC_SITE_MAPS_URL", siteDefaults.mapsUrl),
    mapEmbedUrl: readHttpsUrl(
      "NEXT_PUBLIC_SITE_MAP_EMBED_URL",
      siteDefaults.mapEmbedUrl
    ),
    donate: {
      taxBenefitNote: readEnv(
        "NEXT_PUBLIC_SITE_DONATE_TAX_NOTE",
        siteDefaults.donate.taxBenefitNote
      ),
    },
  };
}
